import React, { useState } from 'react';
import { sendOrionConfiguration } from '../../../services/emailService';
import EmailCard from '../cards/EmailCard';
import toast from "react-hot-toast";

/**
 * DownloadDocumentation component allows users to download selected modules' documentation
 * by entering an email address. The component includes an email form and sends the configuration
 * details (selected modules, price, and delivery time) to the backend upon submission.
 * 
 * @component
 * @param {Object} props - The properties object.
 * @param {Array} props.selectedModules - Array of selected modules to include in the documentation.
 * @param {number} props.price - The total price of the selected modules.
 * @param {string} props.deliveryTime - Estimated delivery time for the selected modules.
 */
const DownloadDocumentation = ({
  /**
  * Array of selected modules to include in the documentation.
  */
  selectedModules,
  /**
   * The total price of the selected modules.
   */
  price,
  /**
   * Estimated delivery time for the selected modules.
   */
  deliveryTime
}) => {
  const [showEmailCard, setShowEmailCard] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false); // 👈 NEW loading state

  /**
   * Handles the display of the EmailCard component when the user clicks the "Download Documentation" button.
   */
  const handleShowEmailCard = () => {
    if (!isLoading) setShowEmailCard(true);
  };

  const handleCloseCard = () => {
    if (!isLoading) setShowEmailCard(false);
  };


  /**
   * Handles the submission of the email and sends the selected modules, price, and delivery time to the backend.
   * Alerts the user in case of success or failure.
   */
  const handleEmailSubmit = async () => {
    if (!email) {
      toast('Please enter your email.');
      return;
    }
    try {
      setIsLoading(true); // 👈 start loading

      const response = await sendOrionConfiguration(email, selectedModules, price, deliveryTime, name);
      if (response.status === 'success') {
        toast('Orion configuration sent successfully!');
        setShowEmailCard(false);
      } else {
        toast.error(response?.message || 'Failed to send configuration.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending configuration.');
    } finally {
      setIsLoading(false); // 👈 end loading
    }
  };

  return (
    <div>
      <button
        onClick={handleShowEmailCard}
        disabled={isLoading} // 👈 disable while loading

        style={{
          display: 'inline-block',
          backgroundColor: 'transparent',
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          textAlign: 'center',
          textDecoration: 'underline' // Add underline to the text
        }}
      >
        Download Documentation
      </button>

      {showEmailCard && (
        <EmailCard
          email={email}
          setEmail={setEmail}
          name={name}
          setName={setName}
          onSubmit={handleEmailSubmit}
          onClose={handleCloseCard}
          isLoading={isLoading} // 👈 pass to child if needed

        />
      )}
    </div>
  );
};

/**
 * Exports the DownloadDocumentation component as the default export.
 */
export default DownloadDocumentation;
